import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "./ui/sheet";
import {
  Loader2, UserRoundPen, KeyRound, ShieldOff, ShieldCheck, Building2,
  Users, ReceiptText, BookOpen, Award, Ticket, ShoppingCart, UserPlus, Trash2,
} from "lucide-react";

const KIND_META = {
  azienda: { label: "Azienda", cls: "bg-brand-blue/10 text-brand-blue border-brand-blue/25", av: "bg-brand-blue/10 text-brand-blue" },
  discente: { label: "Discente", cls: "bg-brand-gold/15 text-brand-gold-deep border-brand-gold/40", av: "bg-brand-gold/15 text-brand-gold-deep" },
  privato: { label: "Privato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", av: "bg-emerald-50 text-emerald-700" },
  super_admin: { label: "Super Admin", cls: "bg-slate-100 text-slate-700 border-slate-300", av: "bg-slate-100 text-slate-600" },
  formatore: { label: "Formatore", cls: "bg-violet-50 text-violet-700 border-violet-200", av: "bg-violet-50 text-violet-700" },
};

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

const orderStatus = {
  pending: { label: "In attesa", cls: "bg-amber-50 text-amber-700" },
  paid: { label: "Pagato", cls: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Annullato", cls: "bg-red-50 text-red-700" },
};

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export default function UserSchedaSheet({
  userId, open, onOpenChange, reloadToken,
  onEditUser, onEditOrg, onResetPassword, onToggleActive, onCreateOrder, onAddLearner,
  onEditLearner, onDeleteLearner, onDeleteUser, onDeleteOrg, onChanged,
}) {
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/overview`);
      setOv(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) load();
  }, [open, userId, reloadToken, load]);

  const assignSeat = async (licenseId, learnerId) => {
    if (!learnerId) return;
    setBusy(true);
    try {
      await api.post(`/admin/licenses/${licenseId}/assign`, { user_id: learnerId });
      toast.success("Posto assegnato.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const unassignSeat = async (licenseId) => {
    setBusy(true);
    try {
      await api.delete(`/admin/licenses/${licenseId}/assign`);
      toast.success("Posto liberato.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const u = ov?.user;
  const kind = ov?.kind;
  const meta = KIND_META[kind] || KIND_META.privato;
  const org = ov?.organization;
  const displayName = kind === "azienda" && org
    ? org.ragione_sociale
    : `${u?.first_name || ""} ${u?.last_name || ""}`.trim() || u?.email || u?.username;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0">
        {loading || !ov ? (
          <div className="flex items-center justify-center h-full text-brand-muted">
            <Loader2 className="animate-spin mr-2" size={18} /> Caricamento…
          </div>
        ) : (
          <div className="p-6">
            <SheetHeader className="mb-5">
              <div className="flex items-center gap-3">
                <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold ${meta.av}`}>
                  {initials(displayName)}
                </span>
                <div className="min-w-0">
                  <SheetTitle className="text-brand-ink truncate flex items-center gap-2">
                    {displayName}
                    <span className={`inline-flex text-xs font-semibold px-2 py-0.5 rounded-sm border ${meta.cls}`}>
                      {meta.label}
                    </span>
                    {u && !u.is_active && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> Disattivato
                      </span>
                    )}
                  </SheetTitle>
                  <SheetDescription className="truncate">
                    {u?.email || u?.username || "—"}
                    {u?.fiscal_code ? ` · ${u.fiscal_code}` : ""}
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>

            {/* Metriche */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
              {kind === "azienda" ? (
                <>
                  <Metric label="Discenti" value={ov.learners.length} />
                  <Metric label="Posti totali" value={ov.seats_total} />
                  <Metric label="Posti liberi" value={ov.seats_available} accent="gold" />
                  <Metric label="Ricavo totale" value={eur(ov.revenue_total)} accent="green" />
                </>
              ) : (
                <>
                  <Metric label="Ordini" value={ov.orders.length} />
                  <Metric label="Corsi" value={ov.courses.length} />
                  <Metric label="Attestati" value={ov.certificates_count} accent="gold" />
                  <Metric label="Speso" value={eur(ov.revenue_total)} accent="green" />
                </>
              )}
            </div>

            {/* Azioni */}
            <div className="flex flex-wrap gap-2 mb-6">
              {(kind === "azienda" || kind === "privato") && (
                <button
                  onClick={() => onCreateOrder?.({
                    buyer: { id: u.id, kind, organization_id: org?.id || u.organization_id || null, name: displayName },
                    learners: ov.learners,
                  })}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm text-white bg-brand-blue hover:bg-brand-blue/90"
                >
                  <ShoppingCart size={15} /> Crea ordine / assegna corsi
                </button>
              )}
              {kind === "azienda" ? (
                <ActionBtn icon={UserRoundPen} label="Modifica dati" onClick={() => org && onEditOrg?.(org)} />
              ) : (
                <ActionBtn icon={UserRoundPen} label="Modifica dati" onClick={() => onEditUser?.(u)} />
              )}
              <ActionBtn icon={KeyRound} label="Reset password" onClick={() => onResetPassword?.(u)} />
              <ActionBtn
                icon={u?.is_active ? ShieldOff : ShieldCheck}
                label={u?.is_active ? "Sospendi" : "Riattiva"}
                onClick={() => onToggleActive?.(u)}
                danger={u?.is_active}
              />
              <ActionBtn
                icon={Trash2}
                label={kind === "azienda" ? "Elimina azienda" : "Elimina"}
                onClick={() => (kind === "azienda" ? (org && onDeleteOrg?.(org)) : onDeleteUser?.(u))}
                danger
              />
            </div>

            {/* Sezioni */}
            {kind === "azienda" && (
              <>
                {org && (
                  <Section icon={Building2} title="Dati di fatturazione">
                    <KV k="Ragione sociale" v={org.ragione_sociale} />
                    <KV k="Partita IVA" v={org.vat_number} />
                    <KV k="Codice fiscale" v={org.tax_code} />
                    <KV k="Codice SDI" v={org.sdi_code} />
                    <KV k="PEC" v={org.pec} />
                    <KV k="Telefono" v={org.phone} />
                  </Section>
                )}
                <Section
                  icon={Users}
                  title={`Discenti (${ov.learners.length})`}
                  action={org && (
                    <button
                      onClick={() => onAddLearner?.(org)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue hover:underline"
                    >
                      <UserPlus size={14} /> Aggiungi discente
                    </button>
                  )}
                >
                  {ov.learners.length === 0 ? (
                    <Empty text="Nessun discente registrato." />
                  ) : (
                    ov.learners.map((l) => (
                      <LearnerRow
                        key={l.id}
                        l={l}
                        onEdit={() => onEditLearner?.(l)}
                        onReset={() => onResetPassword?.({ id: l.id, email: l.email, username: l.fiscal_code, is_active: l.is_active })}
                        onToggle={() => onToggleActive?.({ id: l.id, email: l.email, username: l.fiscal_code, is_active: l.is_active })}
                        onDelete={() => onDeleteLearner?.(l)}
                      />
                    ))
                  )}
                </Section>
                <Section icon={Ticket} title={`Posti (${ov.seats_total})`}>
                  {ov.licenses.filter((x) => x.status !== "revoked").length === 0 ? (
                    <Empty text="Nessun posto acquistato." />
                  ) : (
                    ov.licenses
                      .filter((x) => x.status !== "revoked")
                      .map((lic) => (
                        <LicenseRow
                          key={lic.id}
                          lic={lic}
                          learners={ov.learners}
                          busy={busy}
                          onAssign={(uid) => assignSeat(lic.id, uid)}
                          onUnassign={() => unassignSeat(lic.id)}
                        />
                      ))
                  )}
                </Section>
                <Section icon={ReceiptText} title={`Ordini e ricavi (${ov.orders.length})`}>
                  {ov.orders.length === 0 ? (
                    <Empty text="Nessun ordine." />
                  ) : (
                    ov.orders.map((o) => <OrderLine key={o.id} o={o} />)
                  )}
                </Section>
              </>
            )}

            {(kind === "discente" || kind === "privato" || kind === "super_admin" || kind === "formatore") && (
              <>
                <Section icon={UserRoundPen} title="Anagrafica">
                  <KV k="Nome" v={`${u.first_name} ${u.last_name}`.trim()} />
                  <KV k="Email" v={u.email} />
                  <KV k="Codice fiscale" v={u.fiscal_code} />
                  <KV k="Telefono" v={u.phone} />
                  {org && <KV k="Azienda" v={org.ragione_sociale} />}
                </Section>
                {(kind === "discente" || kind === "privato") && (
                  <Section icon={BookOpen} title={`Corsi (${ov.courses.length})`}>
                    {ov.courses.length === 0 ? (
                      <Empty text="Nessun corso attivo." />
                    ) : (
                      ov.courses.map((c) => <RowLine key={c.course_id} left={c.course_title} sub="" right="Attivo" rightCls="bg-emerald-50 text-emerald-700" />)
                    )}
                  </Section>
                )}
                {kind === "privato" && (
                  <Section icon={ReceiptText} title={`Ordini (${ov.orders.length})`}>
                    {ov.orders.length === 0 ? (
                      <Empty text="Nessun ordine." />
                    ) : (
                      ov.orders.map((o) => <OrderLine key={o.id} o={o} />)
                    )}
                  </Section>
                )}
                {(kind === "discente" || kind === "privato") && ov.certificates_count > 0 && (
                  <Section icon={Award} title="Attestati">
                    <RowLine left={`${ov.certificates_count} attestat${ov.certificates_count === 1 ? "o" : "i"} emess${ov.certificates_count === 1 ? "o" : "i"}`} sub="" right="" />
                  </Section>
                )}
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Metric({ label, value, accent }) {
  const color =
    accent === "green" ? "text-emerald-700" :
    accent === "gold" ? "text-brand-gold-deep" :
    "text-brand-ink";
  return (
    <div className="bg-brand-bg rounded-sm px-3 py-2.5">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-sm border transition-colors " +
        (danger
          ? "border-brand-line text-brand-muted hover:border-red-500 hover:text-red-600"
          : "border-brand-line text-brand-ink hover:border-brand-blue hover:text-brand-blue")
      }
    >
      <Icon size={15} /> {label}
    </button>
  );
}

function Section({ icon: Icon, title, children, action }) {
  return (
    <div className="border border-brand-line rounded-sm mb-4 overflow-hidden">
      <div className="px-4 py-2.5 bg-brand-bg text-sm font-semibold text-brand-ink flex items-center gap-2">
        <Icon size={15} className="text-brand-blue" /> <span className="flex-1">{title}</span>
        {action}
      </div>
      <div className="px-4 divide-y divide-brand-line">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 py-2 text-sm">
      <span className="text-brand-muted">{k}</span>
      <span className="text-brand-ink font-medium text-right truncate">{v || "—"}</span>
    </div>
  );
}

function RowLine({ left, sub, right, rightCls, muted }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-2.5 text-sm ${muted ? "opacity-60" : ""}`}>
      <div className="min-w-0">
        <div className="text-brand-ink truncate">{left}</div>
        {sub ? <div className="text-xs text-brand-muted truncate">{sub}</div> : null}
      </div>
      {right ? (
        rightCls ? (
          <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-sm ${rightCls}`}>{right}</span>
        ) : (
          <span className="shrink-0 text-brand-muted text-xs">{right}</span>
        )
      ) : null}
    </div>
  );
}

function OrderLine({ o }) {
  const st = orderStatus[o.status] || orderStatus.pending;
  const courses = (o.items || []).map((i) => `${i.course_title || "—"} ×${i.seats}`).join(", ");
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 text-sm">
      <div className="min-w-0">
        <div className="text-brand-ink truncate">#{(o.id || "").slice(0, 8)} · {fmtDate(o.created_at)}</div>
        <div className="text-xs text-brand-muted truncate">{courses || "—"}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-brand-ink font-medium">{eur(o.total)}</span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${st.cls}`}>{st.label}</span>
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={
        "p-1.5 rounded-sm border border-brand-line text-brand-muted transition-colors " +
        (danger ? "hover:border-red-500 hover:text-red-600" : "hover:border-brand-blue hover:text-brand-blue")
      }
    >
      <Icon size={15} />
    </button>
  );
}

function LearnerRow({ l, onEdit, onReset, onToggle, onDelete }) {
  const sub = [l.fiscal_code, l.email].filter(Boolean).join(" · ");
  return (
    <div className={`flex items-center justify-between gap-2 py-2.5 text-sm ${!l.is_active ? "opacity-60" : ""}`}>
      <div className="min-w-0">
        <div className="text-brand-ink truncate">
          {`${l.first_name} ${l.last_name}`.trim()}
          {!l.is_active && <span className="ml-2 text-[11px] font-semibold text-slate-500">disattivato</span>}
        </div>
        <div className="text-xs text-brand-muted truncate">
          {sub || "—"} · {l.active_courses} cors{l.active_courses === 1 ? "o" : "i"}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <IconBtn icon={UserRoundPen} title="Modifica" onClick={onEdit} />
        <IconBtn icon={KeyRound} title="Reset password" onClick={onReset} />
        <IconBtn icon={l.is_active ? ShieldOff : ShieldCheck} title={l.is_active ? "Sospendi" : "Riattiva"} onClick={onToggle} />
        <IconBtn icon={Trash2} title="Elimina" onClick={onDelete} danger />
      </div>
    </div>
  );
}

function LicenseRow({ lic, learners, busy, onAssign, onUnassign }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2.5 text-sm">
      <div className="min-w-0">
        <div className="text-brand-ink truncate">{lic.course_title || "—"}</div>
        <div className="text-xs text-brand-muted truncate">
          {lic.status === "assigned"
            ? `Assegnato a ${lic.assigned_user_name || "—"}${lic.started ? " · avviato" : ""}`
            : "Disponibile"}
        </div>
      </div>
      <div className="shrink-0">
        {lic.status === "available" ? (
          <select
            disabled={busy}
            defaultValue=""
            onChange={(e) => onAssign(e.target.value)}
            className="h-8 rounded-sm border border-brand-line text-xs px-2 max-w-[170px]"
          >
            <option value="" disabled>Assegna a…</option>
            {learners.filter((l) => l.is_active).map((l) => (
              <option key={l.id} value={l.id}>{l.first_name} {l.last_name}</option>
            ))}
          </select>
        ) : lic.started ? (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">Avviato</span>
        ) : (
          <button
            disabled={busy}
            onClick={onUnassign}
            className="text-xs font-semibold px-2 py-1 rounded-sm border border-brand-line text-brand-muted hover:border-red-500 hover:text-red-600 disabled:opacity-50"
          >
            Libera
          </button>
        )}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="py-3 text-sm text-brand-muted">{text}</div>;
}
